import { Box } from '@mui/material';
import { FC, useEffect, useState } from 'react';

import { getUsersLatestEulaAgreementStatus } from '../../api/eula';
import ColumnPickerSettingsPopup from './components/ColumnPickerSettingsPopup/ColumnPickerPopup';
import DealerReport from './components/DealerReport/DealerReport';
import Header from './components/Header/Header';
import InsightsContent from './components/InsightsTabContent/InsightsContent';
import MainHeader from './components/MainHeader/MainHeader';
import OpenNewTicketPopup from './components/OpenNewTicketPopup/OpenNewTicketPopup';
import OpenROListPopup from './components/OpenROListPopup/OpenROListPopup';
import RefinementHeader from './components/RefinementHeader/RefinementHeader';
import ScheduleCards from './components/ScheduleCards/ScheduleCards';
import ScheduleListViewHeader from './components/ScheduleListViewHeader/ScheduleListViewHeader';
import TicketDetailsPopup from './components/TicketDetailsPopup/TicketDetailsPopup';
import TicketsCardView from './components/TicketsCardView/TicketsCardView';
import TicketsListView from './components/TicketsListView/TicketsListView';
import useTicketsRefinement from './components/TicketsRefinement/hooks/useTicketsRefinement';
import useTicketsPage from './hooks/useTicketsPage';
import {
  ContentWrapStyled,
  FilterBtnStyled,
  HeaderBoxStyled,
  MessageWrapStyled,
  TicketsPageStyled,
} from './TicketsPage.styled';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import EulaAgreement from 'components/EulaAgreement/EulaAgreement';
import OnboardingTour from 'components/OnboardingTour/OnboardingTour';
import useReleaseNotePopup from 'components/ReleaseNotePopup/hooks/useReleaseNotePopup';
import ReleaseNotePopup from 'components/ReleaseNotePopup/ReleaseNotePopup';
import { useOnboardingTour } from 'contexts/OnboardingTourContext';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { StorageE } from 'declarations/enums/storage';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsViewModeE } from 'declarations/enums/tickets';
import { getDecodedToken } from 'helpers/jwtToken';
import { columns } from 'pages/TicketsPage/components/ColumnPickerSettingsPopup/components/ColumnPickerSettings/mockedColumns';

const TicketsPage: FC = () => {
  const {
    ticketsPages,
    ticketsCount,
    isLoadingTickets,
    isTicketsFetchingError,
    ticketsViewMode,
    fetchTicketsErrorMessage,
    fetchTicketsValidationErrorMessage,
    activeTab,
    isScheduleTicketList,
    setIsScheduleTicketList,
  } = useTickets();
  const { handleSelectTicket } = useTicketsPage({ ticketsPages });

  const { handleClearAllFiltersClick } = useTicketsRefinement();
  const { latestReleaseNote, isLoadingLatestReleaseNote } = useReleaseNotePopup();
  const { isRunningTour, steps, handleJoyrideCallback } = useOnboardingTour();
  const { isMobile } = useResponsive();
  const [displayEulaAgreement, setDisplayEulaAgreement] = useState(false);
  const [displayLatestReleaseNote, setDisplayLatestReleaseNote] = useState(false);
  const [isReportOpened, setReportVisibility] = useState(!isMobile);
  const [isSuggestedAdjustmentsOpened, setSuggestedAdjustmentsOpened] = useState(!isMobile);
  const idToken = localStorage.getItem(StorageE.IdToken);
  const userId = getDecodedToken(idToken)?.['custom:user_id'] || null;

  const handleViewReports = () => {
    setReportVisibility(!isReportOpened);
  };

  const handleToggleSuggestedAdjustments = () => {
    setSuggestedAdjustmentsOpened(prev => !prev);
  };

  useEffect(() => {
    if (activeTab !== ActiveTabTypeE.Schedule) {
      setIsScheduleTicketList(false);
    }
  }, [activeTab, setIsScheduleTicketList]);

  const latestDismissedReleaseNoteId = localStorage.getItem(StorageE.LatestReleaseNoteId);
  const isDismissed = localStorage.getItem(StorageE.IsLatestReleaseNoteDismissed) === 'true';

  useEffect(() => {
    if (!isLoadingLatestReleaseNote && latestReleaseNote) {
      if (!latestDismissedReleaseNoteId || latestDismissedReleaseNoteId !== latestReleaseNote?.id) {
        localStorage.setItem(StorageE.LatestReleaseNoteId, latestReleaseNote?.id || '');
        setDisplayLatestReleaseNote(true);
      } else {
        if (!isDismissed) {
          setDisplayLatestReleaseNote(true);
        }
      }
    }
  }, [isLoadingLatestReleaseNote, latestReleaseNote, latestDismissedReleaseNoteId, isDismissed]);

  useEffect(() => {
    if (isTicketsFetchingError && userId) {
      let isMounted = true;

      getUsersLatestEulaAgreementStatus(userId)
        .then(statusResponse => {
          if (isMounted) {
            if (!statusResponse) {
              setDisplayEulaAgreement(true);
            } else {
              setDisplayEulaAgreement(false);
            }
          }
        })
        .catch(error => {
          if (isMounted) {
            console.error('Error fetching EULA agreement:', error);
          }
        });

      return () => {
        isMounted = false;
      };
    }
  }, [isTicketsFetchingError, userId]);

  if (isTicketsFetchingError) {
    if (displayEulaAgreement) {
      return <EulaAgreement />;
    }

    return (
      <TicketsPageStyled>
        <ContentWrapStyled>
          <HeaderBoxStyled>
            <MainHeader
              handleToggleSuggestedAdjustments={() => {}}
              handleViewReports={handleViewReports}
              isReportOpened={isReportOpened}
              isSuggestedAdjustmentsOpened={false}
            />
            {activeTab !== ActiveTabTypeE.Insights && (
              <>
                <DealerReport isReportOpened={isReportOpened} />
                <Header />
              </>
            )}
          </HeaderBoxStyled>
          <MessageWrapStyled>
            <FilterBtnStyled
              size="md"
              variant="outline-rounded"
              onClick={handleClearAllFiltersClick}
              title="Reset all filters and sorting"
            >
              Clear All Filters
            </FilterBtnStyled>
            <ErrorSection
              statusReason={isTicketsFetchingError ? 'fetchTickets' : null}
              errorDetails={fetchTicketsErrorMessage || fetchTicketsValidationErrorMessage}
            />
          </MessageWrapStyled>
        </ContentWrapStyled>
      </TicketsPageStyled>
    );
  }

  return (
    <TicketsPageStyled>
      <ContentWrapStyled>
        <HeaderBoxStyled>
          <MainHeader
            handleToggleSuggestedAdjustments={handleToggleSuggestedAdjustments}
            handleViewReports={handleViewReports}
            isReportOpened={isReportOpened}
            isSuggestedAdjustmentsOpened={isSuggestedAdjustmentsOpened}
          />
          {activeTab === ActiveTabTypeE.Schedule && !isScheduleTicketList && (
            <ScheduleCards isSuggestedAdjustmentsOpened={isSuggestedAdjustmentsOpened} />
          )}
          {activeTab === ActiveTabTypeE.Schedule && isScheduleTicketList && (
            <ScheduleListViewHeader />
          )}
          {activeTab !== ActiveTabTypeE.Insights && (
            <>
              {activeTab !== ActiveTabTypeE.Schedule && (
                <DealerReport isReportOpened={isReportOpened} />
              )}
              <Header />
              <RefinementHeader />
            </>
          )}
        </HeaderBoxStyled>
        {activeTab === ActiveTabTypeE.Insights ? (
          <InsightsContent />
        ) : (
          <Box sx={{ overflowY: 'auto' }}>
            {ticketsViewMode === TicketsViewModeE.List && (
              <TicketsListView
                columns={columns}
                isLoadingTickets={isLoadingTickets}
                ticketsCount={ticketsCount}
                ticketsPages={ticketsPages}
                onSelectTicket={handleSelectTicket}
              />
            )}
            {ticketsViewMode === TicketsViewModeE.Cards && (
              <TicketsCardView
                isLoadingTickets={isLoadingTickets}
                ticketsPages={ticketsPages}
                onSelectTicket={handleSelectTicket}
              />
            )}
          </Box>
        )}
      </ContentWrapStyled>
      <TicketDetailsPopup />
      <OpenNewTicketPopup />
      <OpenROListPopup />
      <OnboardingTour
        hideCloseButton
        disableOverlayClose
        disableScrollParentFix
        continuous
        showSkipButton
        callback={handleJoyrideCallback}
        run={isRunningTour}
        steps={steps}
      />
      <ColumnPickerSettingsPopup />
      {displayLatestReleaseNote && (
        <ReleaseNotePopup
          displayNotes={displayLatestReleaseNote}
          latestReleaseNote={latestReleaseNote!}
        />
      )}
    </TicketsPageStyled>
  );
};

export default TicketsPage;
